import { styled, css, keyframes } from "@pigment-css/react";
import { clsx } from "clsx";

const pulse = keyframes({
  "0%, 100%": { opacity: 1 },
  "50%": { opacity: 0.4 },
});

const ToggleButton = styled("button")<{ isOn: boolean }>(({ theme }) => ({
  position: "relative",
  width: "44px",
  height: "24px",
  backgroundColor: theme.managementView.colors.neutral["8"],
  borderRadius: theme.managementView.radius["24"],
  border: "none",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  padding: 0,

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral["24"],
  },

  "&:disabled": {
    cursor: "not-allowed",
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.colors.brand[500]}`,
    outlineOffset: "2px",
  },
}));

const loadingStyles = css({
  animation: `${pulse} 1s ease-in-out infinite`,
  pointerEvents: "none",
});

const toggleOnStyles = css(({ theme }) => ({
  backgroundColor: theme.colors.brand[500],

  "&:hover": {
    backgroundColor: theme.colors.brand[600],
  },
}));

const ToggleThumb = styled("span")<{ isOn: boolean }>(({ theme }) => ({
  position: "absolute",
  top: "2px",
  left: "2px",
  width: "20px",
  height: "20px",
  backgroundColor: theme.managementView.colors.neutral["0"],
  borderRadius: "50%",
  transition: "left 0.2s ease",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
}));

const thumbOnStyles = css({
  left: "22px",
});

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  ariaLabel?: string;
}

export const Toggle = ({ checked, onChange, disabled, loading, ariaLabel }: ToggleProps) => {
  const handleClick = () => {
    if (!disabled && !loading) {
      onChange(!checked);
    }
  };

  return (
    <ToggleButton
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      isOn={checked}
      className={clsx(checked && toggleOnStyles, loading && loadingStyles)}
      onClick={handleClick}
      disabled={disabled || loading}
    >
      <ToggleThumb isOn={checked} className={clsx(checked && thumbOnStyles)} />
    </ToggleButton>
  );
};
